import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { SelectOption } from 'src/app/ui/form-field/form-field.component';
import { SharedDialogOptions } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { emailValidator, requiredTrimValidator } from 'src/app/shared/validators/fields-validators';
import { IUser, IUserFormPayload } from '../../../../models/settings.models';

export interface UserFormDialogData {
  user: IUser | null;
  roleOptions: SelectOption[];
  defaultRoleId: number | null;
}

@Component({
  selector: 'app-user-form-dialog',
  templateUrl: './user-form-dialog.component.html',
  styleUrls: ['./user-form-dialog.component.scss'],
})
export class UserFormDialogComponent implements OnInit {
  @Output() save = new EventEmitter<IUserFormPayload>();

  user: IUser | null = null;
  roleOptions: SelectOption[] = [];
  defaultRoleId: number | null = null;

  readonly fullNameControl = new FormControl('', requiredTrimValidator());
  readonly emailControl = new FormControl('', [requiredTrimValidator(), emailValidator()]);
  readonly roleControl = new FormControl<number | null>(null, Validators.required);
  isSubmitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: UserFormDialogData,
    private readonly dialogRef: MatDialogRef<UserFormDialogComponent>,
    private readonly translateService: TranslateService,
  ) {
    this.user = data?.user ?? null;
    this.roleOptions = data?.roleOptions ?? [];
    this.defaultRoleId = data?.defaultRoleId ?? null;
  }

  ngOnInit(): void {
    this.populateForm();
  }

  get isEditMode(): boolean {
    return !!this.user;
  }

  get dialogTitle(): string {
    return this.isEditMode
      ? `${this.translateService.instant('common.edit')} ${this.translateService.instant('user.user')}`
      : this.translateService.instant('user.add_user');
  }

  get saveLabel(): string {
    return this.isEditMode
      ? this.translateService.instant('common.save')
      : this.translateService.instant('common.add');
  }

  get saveIcon(): string {
    return this.isEditMode ? 'pen-cc' : 'plus-cc';
  }

  get isFormInvalid(): boolean {
    return this.fullNameControl.invalid || this.emailControl.invalid || this.roleControl.invalid;
  }

  get dialogOptions(): SharedDialogOptions {
    return {
      size: 'medium',
      cancelText: this.translateService.instant('common.cancel'),
      customConfirmButton: true,
      hideConfirmButton: true,
    };
  }

  submitForm(): void {
    this.fullNameControl.markAsTouched();
    this.emailControl.markAsTouched();
    this.roleControl.markAsTouched();

    if (this.isFormInvalid || this.isSubmitting) return;

    const name = (this.fullNameControl.value ?? '').trim();
    const email = (this.emailControl.value ?? '').trim();
    const roleId = this.roleControl.value;
    if (!roleId) return;

    this.isSubmitting = true;
    this.save.emit({ name, email, roleId });
  }

  close(): void {
    if (this.isSubmitting) return;
    this.dialogRef.close();
  }

  setSubmitting(isSubmitting: boolean): void {
    this.isSubmitting = isSubmitting;
  }

  private populateForm(): void {
    if (this.user) {
      this.fullNameControl.setValue(this.user.name ?? '');
      this.emailControl.setValue(this.user.email ?? '');
      this.roleControl.setValue(this.user.roles?.[0]?.id ?? null);
      return;
    }

    this.fullNameControl.reset('');
    this.emailControl.reset('');
    this.roleControl.reset(null);
    this.applyDefaultRole();
  }

  private applyDefaultRole(): void {
    if (this.roleControl.value) return;

    if (this.defaultRoleId && this.roleOptions.some(o => o.value === this.defaultRoleId)) {
      this.roleControl.setValue(this.defaultRoleId);
      return;
    }

    const firstOption = this.roleOptions[0];
    if (firstOption) {
      this.roleControl.setValue(Number(firstOption.value));
    }
  }
}
